import React, { useState } from "react";
import { Linking, Text, View } from "react-native";
import tw from "twrnc";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

import Callout from "@/components/settings/Callout";
import { SectionTitle } from "@/components/settings/SettingsPage";
import { colors, text } from "@/components/settings/theme";
import countries from "@/components/countries";
import { t, tc, tn } from "@/localization";
import { LiveViewers } from "./EventViewerHours";
import { COUNTED_FROM, atEventTime, breakdownRows, liveSessions, startedBeforeCounts } from "./logic";
import {
  BarList,
  MetricSwitch,
  StatCard,
  StatRow,
  formatNumber,
  formatWatchTime,
  otherLabel,
  unknownLabel,
} from "./reportParts";

// Event Analytics' report, from our own data and kept for good:
//   unique viewers,       the CDN rollup (eventlive-cdk/lambda/viewerHours.ts),
//   views, watch time,    for viewing since 2026-08-19, every player; unique
//   peak, live viewers,   devices over all days come from its sketches
//   breakdowns
//   unique viewers of     eventliveprod-event-viewers (who watched on the event
//   older events          page, every event since 2022)
//   before 2026-08-19     Mux's numbers, imported before Mux dropped them
//                         (eventlive-cdk/scripts/muxHistory.ts)
// Inside the PDF export.

const DEVICE_LABELS = {
  desktop: () => tc("device", "desktop"),
  phone: () => tc("device", "phone"),
  tablet: () => tc("device", "tablet"),
  tv: () => tc("device", "tv"),
  console: () => tc("device", "console"),
};

const plainLabel = (name) =>
  name === "unknown" || !name ? unknownLabel() : name === "other" ? otherLabel() : name;

const countryLabel = (code) =>
  code === "unknown" || !code ? unknownLabel() : code === "other" ? otherLabel() : countries[code] || code;

// "US|California" -> "California, US"; Mux's regions have no country.
const regionLabel = (name) => {
  if (name === "unknown" || name === "other" || !name) return plainLabel(name);
  const i = name.indexOf("|");
  return i < 0 ? name : `${name.slice(i + 1)}, ${name.slice(0, i)}`;
};

// "phone" -> "Phone"; "tv" -> "TV".
const deviceLabel = (name) => {
  if (!DEVICE_LABELS[name]) return plainLabel(name);
  const label = DEVICE_LABELS[name]();
  return label === "tv" ? "TV" : label.charAt(0).toUpperCase() + label.slice(1);
};

const DIMENSIONS = [
  { field: "countries", title: () => t("Top Countries"), label: countryLabel },
  { field: "regions", title: () => t("Top Regions / States"), label: regionLabel, regions: true },
  { field: "device_types", title: () => t("Device Categories"), label: deviceLabel },
  { field: "browsers", title: () => t("Browsers and apps"), label: plainLabel },
  { field: "operating_systems", title: () => t("Operating Systems"), label: plainLabel },
];

const viewersLabel = (n) => tn("{count} viewer", "{count} viewers", n, { count: formatNumber(n) });
// English has always said "1 views" here; Spanish gets its singular.
const viewsLabel = (n) => tn("{count} views", "{count} views", n, { count: formatNumber(n) });

// The switch's metrics: views, viewers (the CDN's devices, Mux's viewer ids), watch time.
const METRICS = () => [
  { value: "views", label: t("Views"), icon: "eye-outline" },
  { value: "viewers", label: t("Viewers"), icon: "people-outline" },
  { value: "hours", label: t("Watch Time"), icon: "time-outline" },
];

const Breakdowns = ({ source, metric, mux }) =>
  DIMENSIONS.map((d) => {
    const field = metric === "hours" ? "hours" : metric === "views" ? "views" : mux ? "viewers" : "devices";
    const rows = breakdownRows(source?.[d.field], field, 10).map((r) => ({
      key: r.name,
      label: d.label(r.name),
      value: r.value,
      display:
        metric === "hours"
          ? formatWatchTime(r.hours)
          : metric === "views"
          ? viewsLabel(r.views)
          : viewersLabel(mux ? r.viewers : r.devices),
    }));
    // Regions: DB-IP's licence asks for credit where they're shown.
    const note =
      d.regions && !mux && rows.length ? (
        <Text style={text.hint}>
          {t("Regions:")}{" "}
          <Text style={text.link} onPress={() => Linking.openURL("https://db-ip.com")}>
            {t("IP Geolocation by DB-IP")}
          </Text>
        </Text>
      ) : null;
    return <BarList key={d.field} title={d.title()} rows={rows} note={note} />;
  });

const Earlier = ({ mux, metric, setMetric }) => {
  const to = moment.utc(mux.to || `${COUNTED_FROM}T00:00:00Z`).format(t("MMM D, YYYY"));
  return (
    <View style={tw`mt-4`}>
      <SectionTitle
        title={t("Before {date}", { date: to })}
        description={t(
          "From our previous analytics, which counted browsers only, saved for good before they expired."
        )}
      />
      <StatRow wide>
        <StatCard title={t("Total Views")} value={formatNumber(mux.views)} />
        <StatCard title={t("Unique Viewers")} value={formatNumber(mux.unique_viewers)} />
        <StatCard
          title={t("Total Watch Time")}
          value={formatWatchTime(mux.watch_hours)}
          subtitle={t("Live {live} · Replay {replay}", {
            live: formatWatchTime(mux.live_hours),
            replay: formatWatchTime(mux.vod_hours),
          })}
        />
      </StatRow>
      <MetricSwitch value={metric} onChange={setMetric} options={METRICS()} />
      <Breakdowns source={mux} metric={metric} mux />
    </View>
  );
};

const EventReport = ({ event, viewerHours, stats }) => {
  const [metric, setMetric] = useState("viewers");
  const [earlierMetric, setEarlierMetric] = useState("viewers");
  const data = viewerHours.data;
  const counted = !!data?.counted;
  // Every player's devices when the CDN counted the event; otherwise the
  // event page's viewers.
  const cdnUniques = counted && data.unique_devices != null;
  const unique = cdnUniques ? data.unique_devices : stats?.unique_viewers || 0;
  const live = Math.min(unique, cdnUniques ? data.live_unique_devices || 0 : stats?.live_viewers || 0);
  // The busiest live minute, in the event's time zone.
  const peak = liveSessions(data?.days).reduce((a, s) => (!a || s.peak > a.peak ? s : a), null);
  const peakAt = peak ? atEventTime(peak.peakAt, event?.event_timezone) : null;

  return (
    <View>
      <StatRow wide>
        <StatCard
          title={t("Unique Viewers")}
          value={formatNumber(unique)}
          subtitle={t("{live} live · {replay} replay only", {
            live: formatNumber(live),
            replay: formatNumber(Math.max(0, unique - live)),
          })}
        />
        {counted && <StatCard title={t("Total Views")} value={formatNumber(data.views)} />}
        {counted && (
          <StatCard
            title={t("Total Watch Time")}
            value={formatWatchTime(data.hours)}
            subtitle={
              unique > 0
                ? t("{time} per viewer", { time: formatWatchTime(data.hours / unique) })
                : null
            }
          />
        )}
        {counted && data.peak_devices > 0 && (
          <StatCard
            title={t("Peak live viewers")}
            value={formatNumber(data.peak_devices)}
            subtitle={peakAt ? `${peakAt.format(t("MMM D"))}, ${peakAt.format(t("h:mm A"))}` : null}
          />
        )}
      </StatRow>

      {counted && data.log_gaps?.length > 0 && (
        <Callout tone="warning" style={tw`mb-4`}>
          {t(
            "Live viewing during {times} isn't fully counted: our video network didn't save all its logs for that time.",
            {
              times: data.log_gaps
                .flatMap((g) => (g.hours || []).map((h) => [g.day, h]))
                .map(([day, h]) => {
                  const at = atEventTime(Date.parse(`${day}T00:00:00Z`) + h * 3600000, event?.event_timezone);
                  const end = at.clone().add(1, "hour");
                  return `${at.format(t("MMM D"))}, ${at.format(t("h:mm A"))}–${end.format(t("h:mm A"))}`;
                })
                .join("; "),
            }
          )}
        </Callout>
      )}

      {counted && startedBeforeCounts(event) && (
        <Text style={[text.hint, tw`-mt-2 mb-4`]}>
          {data?.mux
            ? t("Counted from {date}; the viewing before it is further down.", {
                date: moment.utc(COUNTED_FROM).format(t("MMM D, YYYY")),
              })
            : t("Counted from {date}; the viewing before it wasn't counted.", {
                date: moment.utc(COUNTED_FROM).format(t("MMM D, YYYY")),
              })}
        </Text>
      )}

      {!counted && !data?.mux && !viewerHours.loading && (
        <Callout tone="info" style={tw`mb-4`}>
          {startedBeforeCounts(event)
            ? t(
                "Watch time, views, countries and devices are counted from {date}. Unique viewers are counted for every event.",
                { date: moment.utc(COUNTED_FROM).format(t("MMM D, YYYY")) }
              )
            : t("Analytics are updated every hour. They show here within an hour of people watching.")}
        </Callout>
      )}

      {counted && (
        <>
          <LiveViewers event={event} state={viewerHours} style={tw`mb-6`} />
          <MetricSwitch value={metric} onChange={setMetric} options={METRICS()} />
          <Breakdowns source={data} metric={metric} />
          <Text style={[text.hint, tw`-mt-2 mb-2`]}>
            {t(
              "Viewers here are devices on each day: someone watching on two days counts twice. Counted every hour from the video our CDN delivered, on every player."
            )}
          </Text>
        </>
      )}

      {!!data?.mux && <Earlier mux={data.mux} metric={earlierMetric} setMetric={setEarlierMetric} />}
    </View>
  );
};

export default EventReport;
